<template>
  <div class="signature-canvas-container" ref="container" :data-testid="testId">
    <!-- Режим просмотра -->
    <div v-if="isViewMode" class="signature-view">
      <img v-if="imageUrl" :src="imageUrl" alt="Signature" class="signature-image" />
      <span v-else class="no-signature">No signature</span>
    </div>

    <template v-else
      ><div class="w-full flex justify-content-end pb-2">
        <a-button-group size="small" class="right-upper-corner">
          <a-tooltip title="Clear Signature">
            <a-button icon="redo" :data-testid="clearButtonTestId" @click="clearCanvas" />
          </a-tooltip>
          <a-tooltip title="Undo Signature">
            <a-button icon="left" :data-testid="undoButtonTestId" @click="undo" />
          </a-tooltip>
        </a-button-group>
      </div>
      <div class="signature-edit">
        <canvas
          ref="canvas"
          class="signature-canvas"
          @mousedown="startDrawing"
          @mousemove="draw"
          @mouseup="stopDrawing"
          @mouseout="stopDrawing"
          @touchstart="startDrawingTouch"
          @touchmove="drawTouch"
          @touchend="stopDrawing"
          @touchcancel="stopDrawing"
          :data-testid="canvasElementTestId"
        ></canvas></div
    ></template>
    <!-- Режим редактирования -->
  </div>
</template>

<script>
import { Button, Tooltip } from 'ant-design-vue'

export default {
  name: 'SignatureCanvas',
  components: {
    'a-button': Button,
    'a-button-group': Button.Group,
    'a-tooltip': Tooltip
  },
  props: {
    imageUrl: { type: String, default: null },
    signedDate: { type: String, default: null },
    penColor: { type: String, default: '#000000' },
    backgroundColor: { type: String, default: '#f4f4f4' },
    scaleRatio: {
      type: Number,
      default: 1
    },
    testId: { type: String, default: undefined },
    canvasElementTestId: { type: String, default: undefined },
    clearButtonTestId: { type: String, default: undefined },
    undoButtonTestId: { type: String, default: undefined }
  },
  data() {
    return {
      isDrawing: false,
      ctx: null,
      history: [],
      displayWidth: 0,
      displayHeight: 0,
      internalScale: 1,
      resizeObserver: null
    }
  },
  computed: {
    isViewMode() {
      return !!this.signedDate && !!this.imageUrl
    }
  },
  watch: {
    isViewMode: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          if (!this.isViewMode) {
            this.initCanvas()
          }
        })
      }
    }
  },
  methods: {
    getScale() {
      return this.scaleRatio !== null ? this.scaleRatio : window.devicePixelRatio || 1
    },

    updateCanvasSize() {
      const container = this.$refs.container
      if (!container) return

      // Ширина контейнера (в CSS пикселях)
      const newWidth = container.clientWidth
      const newHeight = newWidth / 2 // 🔑 ВАЖНО: высота = 1/2 ширины

      if (newWidth === 0) return

      this.displayWidth = newWidth
      this.displayHeight = newHeight

      const canvas = this.$refs.canvas
      if (!canvas) return

      this.internalScale = this.getScale()

      // Физические размеры (устройство)
      canvas.width = this.displayWidth * this.internalScale
      canvas.height = this.displayHeight * this.internalScale

      // CSS-размеры
      canvas.style.width = this.displayWidth + 'px'
      canvas.style.height = this.displayHeight + 'px'

      const ctx = canvas.getContext('2d')
      this.ctx = ctx
      ctx.scale(this.internalScale, this.internalScale)

      // Фон
      ctx.fillStyle = this.backgroundColor
      ctx.fillRect(0, 0, this.displayWidth, this.displayHeight)

      // Стиль пера
      ctx.strokeStyle = this.penColor
      ctx.lineWidth = 2 / this.internalScale
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
    },

    initCanvas() {
      this.updateCanvasSize()
      this.history = []
      this.saveState()
    },

    saveState() {
      if (!this.ctx || !this.$refs.canvas) return
      const data = this.$refs.canvas.toDataURL('image/png')
      this.history.push(data)
      if (this.history.length > 10) this.history.shift()
    },

    restoreState() {
      if (this.history.length === 0) return
      const url = this.history[this.history.length - 1]
      const img = new Image()
      img.onload = () => {
        this.ctx.clearRect(0, 0, this.displayWidth, this.displayHeight)
        this.ctx.drawImage(img, 0, 0, this.displayWidth, this.displayHeight)
      }
      img.src = url
    },

    getPos(e) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      return {
        x: (e.clientX - rect.left) * (this.displayWidth / rect.width),
        y: (e.clientY - rect.top) * (this.displayHeight / rect.height)
      }
    },

    startDrawing(e) {
      if (this.isViewMode) return
      this.isDrawing = true
      const pos = this.getPos(e)
      this.ctx.beginPath()
      this.ctx.moveTo(pos.x, pos.y)
    },

    draw(e) {
      if (!this.isDrawing || this.isViewMode) return
      const pos = this.getPos(e)
      this.ctx.lineTo(pos.x, pos.y)
      this.ctx.stroke()
    },

    stopDrawing() {
      if (this.isDrawing && !this.isViewMode) {
        this.isDrawing = false
        this.ctx.closePath()
        this.saveState()
      }
    },

    startDrawingTouch(e) {
      if (this.isViewMode) return
      e.preventDefault()
      const touch = e.touches[0]
      this.startDrawing({
        clientX: touch.clientX,
        clientY: touch.clientY
      })
    },

    drawTouch(e) {
      if (this.isViewMode) return
      e.preventDefault()
      const touch = e.touches[0]
      this.draw({
        clientX: touch.clientX,
        clientY: touch.clientY
      })
    },

    clearCanvas() {
      this.initCanvas()
    },

    undo() {
      if (this.history.length > 1) {
        this.history.pop()
        this.restoreState()
      }
    },

    getDataURL() {
      if (this.isViewMode) {
        return this.imageUrl || null
      }

      const canvas = this.$refs.canvas
      if (!canvas) return { isEmpty: true, data: null }

      const ctx = canvas.getContext('2d')
      const w = this.displayWidth
      const h = this.displayHeight
      const imageData = ctx.getImageData(0, 0, w, h)
      const data = imageData.data

      const bg = this.hexToRgb(this.backgroundColor)
      let isEmpty = true
      const threshold = 10

      for (let i = 0; i < data.length; i += 4) {
        if (
          Math.abs(data[i] - bg.r) > threshold ||
          Math.abs(data[i + 1] - bg.g) > threshold ||
          Math.abs(data[i + 2] - bg.b) > threshold
        ) {
          isEmpty = false
          break
        }
      }

      return { isEmpty, url: canvas.toDataURL('image/png') }
    },

    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
          }
        : { r: 244, g: 244, b: 244 }
    },

    handleResize() {
      if (!this.isViewMode) {
        this.initCanvas()
      }
    }
  },

  mounted() {
    this.$nextTick(() => {
      if (!this.isViewMode) {
        this.initCanvas()
      }

      const container = this.$refs.container
      if (!container) {
        console.warn('Container ref not found')
        return
      }

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.handleResize()
        })
        this.resizeObserver.observe(container) // ✅ now safe
      } else {
        window.addEventListener('resize', this.handleResize)
      }
    })
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.handleResize)
    }
  }
}
</script>

<style scoped>
.signature-canvas-container {
  width: 100%;
  height: auto;
  aspect-ratio: 2/1;
  position: relative;
}

.signature-view {
  width: 100%;
  height: 100%;
}

.signature-view,
.signature-edit {
  width: 100%;
  background-color: #f4f4f4;
  border-radius: 4px;
  overflow: hidden;
  /* Высота будет задана динамически через JS */
}

.signature-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.no-signature {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #999;
  font-style: italic;
}

.signature-canvas {
  display: block;
  background: transparent;
  cursor: crosshair;
}

.signature-controls {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}

.right-upper-corner {
  text-align: right;
  font-size: 14px;
}
</style>
